import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { PreviewState } from '../../../stores/chatManagementStore';
import { usePlatformStore } from '../../../stores/platformStore';

export type PreviewSectionHandle = {
	collapse: () => void;
	expand: () => void;
	toggle: () => void;
	scrollIntoView: () => void;
};

/*
    A double-buffered iframe preview component.

    To avoid a "white flash" when the preview content updates, this component
    maintains two hidden iframes. New content is loaded into the inactive
    iframe. Once loaded, it's made visible, and the previously active iframe
    is hidden, creating a seamless transition for the user.
*/
export const PreviewSection = forwardRef<
	PreviewSectionHandle,
	{
		previewState: PreviewState;
		// eslint-disable-next-line react/require-default-props
		initialCollapsed?: boolean;
		// eslint-disable-next-line react/require-default-props
		onScrollToBottom?: () => void;
	}
>(function PreviewSection(
	{ previewState, initialCollapsed = false, onScrollToBottom = undefined },
	ref,
) {
	const [collapsed, setCollapsed] = useState(initialCollapsed);
	const [activeIdx, setActiveIdx] = useState<0 | 1>(0);
	// Web wheel events get trapped by the iframe and don't chain to the parent
	// scroll panel; pointer-events:none lets them fall through. Desktop scrolls
	// fine without this and we want to keep clicks/selection there.
	const isDesktop = usePlatformStore((s) => s.isDesktop());
	const activeIframeClass = isDesktop ? '' : 'pointer-events-none';
	// Use a ref for the active index so the srcdoc effect never has it as a
	// dependency (that would create an infinite swap loop: content loads →
	// activeIdx changes → effect re-runs → content loads again).
	const activeIdxRef = useRef<0 | 1>(0);
	const [iframeHeight, setIframeHeight] = useState(320);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const iframeARef = useRef<HTMLIFrameElement | null>(null);
	const iframeBRef = useRef<HTMLIFrameElement | null>(null);

	useImperativeHandle(
		ref,
		() => ({
			collapse: () => setCollapsed(true),
			expand: () => setCollapsed(false),
			toggle: () => setCollapsed((v) => !v),
			scrollIntoView: () =>
				rootRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				}),
		}),
		[],
	);

	// On content change, load into inactive buffer and swap on load
	useEffect(() => {
		const nextHtml =
			previewState.content ||
			'<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body></body></html>';
		const nextIdx: 0 | 1 = activeIdxRef.current === 0 ? 1 : 0;
		const nextRef = nextIdx === 0 ? iframeARef : iframeBRef;
		const el = nextRef.current;
		if (!el) return undefined;

		let swapped = false;
		const handleLoad = () => {
			if (swapped) return;
			swapped = true;

			try {
				const height =
					el.contentDocument?.documentElement?.scrollHeight;
				if (height) setIframeHeight(height);
			} catch {
				// cross-origin guard
			}

			activeIdxRef.current = nextIdx;
			setActiveIdx(nextIdx);
			try {
				el.contentWindow?.focus();
			} catch {
				// cross-origin guard
			}
			el.removeEventListener('load', handleLoad);
		};

		el.addEventListener('load', handleLoad);
		try {
			(el as HTMLIFrameElement & { srcdoc: string }).srcdoc = nextHtml;
		} catch {
			el.setAttribute('srcdoc', nextHtml);
		}

		return () => {
			el.removeEventListener('load', handleLoad);
		};
	}, [previewState.content]);

	return (
		<div
			ref={rootRef}
			className="bg-neutral-500 rounded-small p-sm border border-neutral-400"
		>
			<div className="flex items-center justify-between pb-sm">
				<h4 className="text-sm font-medium text-neutral-100 m-0">
					Preview
				</h4>
				<div className="flex items-center justify-end gap-lg">
					<button
						className="bg-transparent border-0 cursor-pointer text-neutral-200 hover:text-neutral-white p-0 m-0"
						title="Jump to bottom"
						aria-label="Jump to bottom"
						onClick={(e) => {
							if (onScrollToBottom) {
								onScrollToBottom();
								return;
							}
							let el: HTMLElement | null =
								e.currentTarget as HTMLElement;
							while (el && el.scrollHeight <= el.clientHeight) {
								el = el.parentElement;
							}
							el?.scrollTo({
								top: el.scrollHeight,
								behavior: 'smooth',
							});
						}}
						type="button"
					>
						<i className="fas fa-arrow-down" />
					</button>
					<button
						className="bg-transparent border-0 cursor-pointer text-neutral-200 hover:text-neutral-white p-0 m-0"
						title={
							collapsed ? 'Expand preview' : 'Minimize preview'
						}
						aria-label={
							collapsed ? 'Expand preview' : 'Minimize preview'
						}
						onClick={() => setCollapsed((v) => !v)}
						type="button"
					>
						<i
							className={`fas ${collapsed ? 'fa-plus' : 'fa-minus'}`}
						/>
					</button>
				</div>
			</div>
			<div
				className={`bg-neutral-800 rounded-small border border-neutral-600 overflow-hidden relative transition-all duration-200 ${
					collapsed ? 'h-0 opacity-0 pointer-events-none' : ''
				}`}
				aria-hidden={collapsed}
				id="export-preview"
			>
				{(() => {
					if (previewState.content) {
						return (
							<div
								className="relative w-full"
								style={{ height: iframeHeight }}
							>
								<iframe
									ref={iframeARef}
									className={`w-full h-full border-0 absolute inset-0 ${activeIdx === 0 ? activeIframeClass : 'invisible pointer-events-none'}`}
									title="Export Preview A"
									sandbox="allow-same-origin"
									scrolling="no"
								/>
								<iframe
									ref={iframeBRef}
									className={`w-full h-full border-0 absolute inset-0 ${activeIdx === 1 ? activeIframeClass : 'invisible pointer-events-none'}`}
									title="Export Preview B"
									sandbox="allow-same-origin"
									scrolling="no"
								/>
							</div>
						);
					}
					if (previewState.error) {
						return (
							<div className="flex items-center justify-center h-32 text-red-400">
								<div className="text-center">
									<i className="fas fa-exclamation-triangle text-xl mb-xs" />
									<p className="text-xs">{previewState.error}</p>
								</div>
							</div>
						);
					}
					return (
						<div className="flex items-center justify-center h-32 text-neutral-500">
							<div className="text-center">
								<i className="fas fa-eye text-xl mb-xs" />
								<p className="text-xs">Preview will appear here</p>
							</div>
						</div>
					);
				})()}
			</div>
		</div>
	);
});
